import type { Polygon, Position } from "geojson";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import {
  RegistroFincaBorrador,
  RegistroFincaBorradorLote,
} from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import { parseWizardErrorMessage } from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import { registroFincaTempGrpcService } from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { LoteSplitMapRender } from "../map/LoteSplitMapRender";
import type { OperacionVerticeServidor } from "../map/LoteVertexEditEngine";
import {
  fx_cajaDePoligono,
  fx_unirCajas,
  LoteSplitSeed,
  LoteSplitState,
} from "../map/LoteSplitState";
import { LotesTempSync } from "../map/LotesTempSync";
import { Paso4InstructionsRender } from "../map/Paso4InstructionsRender";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import template from "./registroFincaPaso4Template.html";

/** Nombre del paso, que el mapa pinta como primer card de la fila de avisos y
 *  no oculta nunca (ver `LoteSplitMapRender`/`fx_filaAvisos`). Vive acá y no
 *  en el mapa, mismo criterio que `AVISO_PASO` del paso 3: es el enunciado de
 *  ESTE paso del wizard, no algo que el componente de mapa pueda saber. */
const AVISO_PASO = "2) Dibujar los lotes del predio.";

/** Lo que el WMS dibuja de un lote, para saber si cambió (ver
 *  `RegistroFincaPaso4Render._huellasDeLotes`). */
type HuellaLote = {
  /** Nombre y coordenadas: si cambia cualquiera de los dos, cambia la imagen. */
  firma: string;
  caja: [number, number, number, number] | null;
};


/** Paso 4 del wizard de registro: dividir el AOI guardado en el paso 3 en
 *  lotes (la línea se traza acá y el corte lo aplica el servidor, ver
 *  `dividirLoteAsync` y `LoteSplitGeometria`).
 *
 *  **Solo "Dividir" y "Unir" escriben en la base** (más los nombres que el
 *  usuario escribe): cada corte y cada unión se guardan al momento, por id
 *  del lote (`LotesTempSync`). Abrir el paso, "Volver" y "Continuar" no
 *  escriben lotes por su cuenta: solo esperan lo pendiente, y "Continuar"
 *  confirma el paso. Si una operación falla, la pantalla se recarga desde
 *  la base — manda la base, nunca lo que haya quedado en memoria.
 *
 *  Los lotes que no se tocan conservan la asignación del paso 5 que ya
 *  tenían (`LoteSplitEntry.asignacion`); una pieza nueva, salida de un corte
 *  o de una unión, nace sin asignar.
 *
 *  **Los lotes se leen de la base al montar** (`leerAsync`): los crea el
 *  "Continuar" del paso 3 (`ConfirmarPiezas`). Si no hubiera ninguno, se
 *  avisa y se pide volver al paso 3; este paso no los inventa. */
export class RegistroFincaPaso4Render extends BaseRegistroFincaPasoRender {
  private readonly _state = new LoteSplitState();
  private _mapRender: LoteSplitMapRender | null = null;
  /** Los polígonos padre del registro, en memoria y sin renderizar: el
   *  perímetro contra el que se pega y se autocierra el modo Dibujar. Los trae
   *  `_cargarDesdeBase`, que ya los pedía. */
  private _poligonosPadre: readonly Polygon[] = [];
  private _sync: LotesTempSync | null = null;
  private _disposed = false;
  private _guardando = false;

  constructor(
    private readonly _borrador: RegistroFincaBorrador,
    private readonly _onVolver: () => void,
    private readonly _onLotesListos: (borrador: RegistroFincaBorrador) => void,
  ) {
    super(_borrador.clienteId);
  }

  renderBody(): string {
    return template;
  }

  connectBody(): void {
    const registroId = this._borrador.registroId;
    if (!registroId) return;

    this._sync = new LotesTempSync(
      registroId,
      this._state,
      this._alFallarGuardado,
      // Los lotes los dibuja GeoServer desde la base, así que el mapa se
      // repinta cuando el servidor CONFIRMÓ el cambio, no cuando el usuario lo
      // hizo. Vale para las tres cosas que cambian lo que se ve: un corte, una
      // unión o un borrado (la geometría) y un nombre nuevo (la etiqueta, que
      // la escribe el `TextSymbolizer` del SLD desde la columna `nombre`).
      // Sólo la zona que tocó la operación (2026-10-27; para revertir,
      // `refrescarLotes()` sin la zona).
      (zona) => this._mapRender?.refrescarLotes(zona),
    );
    this._sync.conectar();
    // Todo cambio de lotes (cortar, unir, mover un vértice, quitar uno) mueve

    this._cargarDesdeBase().then((cargado) => {
      if (!cargado || this._disposed) return;
      const mapMount = document.getElementById("registroFincaPaso4MapMount");
      // El `registroId` habilita la capa WMS de polígonos padre: el paso 4
      // muestra debajo de los lotes los contornos que se dibujaron en el paso
      // 3, como referencia de dónde está el perímetro.
      this._mapRender = new LoteSplitMapRender(
        this._state,
        AVISO_PASO,
        registroId,
        // El ortomosaico sólo en edición: es la referencia real del predio contra
        // la que se corrigen los lotes, y sólo existe si la finca ya está creada.
        this._borrador.esEdicion,
      );
      this._mapRender.setOnDividir(this._alDividir);
      this._mapRender.setOnUnir(this._alUnir);
      this._mapRender.setOnDibujado(this._alDibujar);
    this._mapRender.setOnValidarAutocierre(this._validarAutocierre);
      this._mapRender.setOnVertice(this._alEditarVertice);
      this._mapRender.setOnLoteSeleccionado((id) => {
        if (id !== null) this._instructions?.enfocarInputDe(id);
      });
      // Los polígonos padre, **en memoria y sin renderizar**: los trajo la
      // misma lectura que los lotes (`_cargarDesdeBase`), así que no cuestan un
      // viaje aparte. Acá no se dibujan —lo que se ve son los lotes—; son el
      // perímetro contra el que la herramienta Dibujar pega el trazo y se
      // autocierra (ver `LoteSplitMapRender.setPoligonosPadre`).
      this._mapRender.setPoligonosPadre(this._poligonosPadre);
      if (mapMount) {
        mapMount.innerHTML = this._mapRender.render();
        this._mapRender.connect();
      }
    });
  }

  /** Lee los lotes de la base y los pone en pantalla tal cual. Se usa al
   *  montar y para recargar después de una falla. Devuelve si cargó. */
  private async _cargarDesdeBase(): Promise<boolean> {
    try {
      const leido = await registroFincaTempGrpcService.leerAsync(this._borrador);
      if (this._disposed) return false;

      const lotes = leido.lotes.filter((lote) => lote.id);
      if (lotes.length === 0) {
        throw new Error("La finca todavía no tiene lotes guardados. Vuelva al paso 3 y pulse Continuar.");
      }

      // La geometría de los polígonos padre, tal como está en la base. Esta
      // lectura ya la traía y se descartaba; ahora se guarda para el snap y el
      // autocierre del modo Dibujar. Se refresca en cada recarga, que es
      // cuando el servidor pudo haber recalculado el padre desde los lotes.
      this._poligonosPadre = leido.poligonosPadre;
      this._mapRender?.setPoligonosPadre(this._poligonosPadre);

      this._state.init(lotes.map(aSeed));
      this._sync?.adoptar();
      return true;
    } catch (error) {
      serviceProvider
        .getErrorService()
        .addError("Error al leer los lotes", parseWizardErrorMessage(error));
      return false;
    }
  }

  /** El usuario terminó la línea de corte: se manda al servidor, que es el que
   *  parte el lote.
   *
   *  **Se espera la respuesta y se adopta tal cual.** El corte puede además
   *  insertarle un vértice al borde de un lote vecino (el sellado de bordes),
   *  así que lo que vuelve son TODOS los lotes del registro y con eso se
   *  reemplaza el estado — no se parchea la memoria. Es el mismo criterio de
   *  `_cargarDesdeBase`: manda la base, nunca la memoria.
   *
   *  Antes de esto el corte se hacía en el navegador y el estado cambiaba al
   *  instante, con `LotesTempSync` mandando las dos mitades después. Ahora hay
   *  un viaje de red entre el click en "finalizar" y el lote partido; a cambio,
   *  lo que se ve es exactamente lo que quedó guardado. */
  private _alDividir = (loteTempId: string, linea: Position[]): void => {
    const registroId = this._borrador.registroId;
    if (!registroId) return;

    void (async () => {
      try {
        // Las operaciones en vuelo primero: un corte sobre un lote cuyo alta o
        // cuyo renombre todavía no llegó se aplicaría sobre otra cosa.
        await this._sync?.confirmarAsync();
        const { lotes } = await registroFincaTempGrpcService.dividirLoteAsync(
          registroId,
          loteTempId,
          linea,
        );
        if (this._disposed) return;

        this._lotesDeLaBase(lotes);
      } catch (error) {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("No se pudo dividir", parseWizardErrorMessage(error));
      }
    })();
  };

  /** El usuario terminó de dibujar un lote: el trazo se manda al servidor, que
   *  es el que lo convierte en lote.
   *
   *  **Llegan los puntos clickeados, no un polígono.** La forma la decide el
   *  servidor: antes el navegador armaba el anillo y lo validaba con turf
   *  (`LoteSplitState.agregarLote`, la última validación geométrica que quedaba
   *  acá) y lo mandaba como un lote cualquiera.
   *
   *  **El trazo puede venir abierto**, y entonces el servidor lo cierra contra
   *  el borde del polígono padre que tiene guardado. Para el cliente es el mismo
   *  rpc; si no se puede cerrar, vuelve `TRAZO_ABIERTO` con su mensaje.
   *
   *  **El padre y el perímetro los rehace el servidor** en el mismo rpc
   *  (`RecalcularAreaDesdeLotesAsync`), y vale para todas las operaciones del
   *  paso: el padre es la unión de los lotes, así que un lote dibujado puede
   *  agrandarlo. Antes esa unión la hacía el navegador con turf después de
   *  cada cambio, y con decenas de lotes congelaba la app unos dos segundos
   *  justo al terminar de dibujar. */
  /** ¿El trazo en curso se puede autocerrar? Lo decide el servidor
   *  (`ValidarAutocierreLote`), que corre la misma cuenta que `DibujarLote`
   *  pero sin escribir nada. Ver `LoteSplitToolbar._onAutocierrePosible`. */
  private _validarAutocierre = async (
    puntos: Position[],
  ): Promise<{ valido: boolean; areaHas: number }> => {
    const registroId = this._borrador.registroId;
    if (!registroId) return { valido: false, areaHas: 0 };

    try {
      const { valido, areaHas } =
        await registroFincaTempGrpcService.validarAutocierreLoteAsync(
          registroId,
          puntos,
        );
      return { valido: !this._disposed && valido, areaHas };
    } catch {
      // Sin respuesta no se autocierra. No se avisa: mientras se dibuja, que un
      // trazo todavía no cierre es normal, no un error del usuario.
      return { valido: false, areaHas: 0 };
    }
  };

  private _alDibujar = (trazo: Position[]): void => {
    const registroId = this._borrador.registroId;
    if (!registroId) return;

    void (async () => {
      try {
        // Las operaciones en vuelo primero, por lo mismo que en `_alDividir`.
        await this._sync?.confirmarAsync();
        const { lotes } = await registroFincaTempGrpcService.dibujarLoteAsync(
          registroId,
          trazo,
        );
        if (this._disposed) return;

        this._lotesDeLaBase(lotes);
      } catch (error) {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("No se pudo crear el lote", parseWizardErrorMessage(error));
      }
    })();
  };

  /** El usuario confirmó unir dos lotes: se manda al servidor, que es el que
   *  decide si son adyacentes y, si lo son, los une.
   *
   *  El rechazo por no ser vecinos llega como un error con su mensaje ya armado
   *  (`LOTES_NO_ADYACENTES`, que nombra los dos lotes), así que acá sólo se
   *  muestra. Antes el cliente lo decidía y redactaba el texto por su cuenta.
   *
   *  Como en el corte, se adopta la respuesta tal cual: la unión reemplaza dos
   *  lotes por uno y el que queda estrena nombre, así que lo que vuelve son
   *  todos los lotes del registro. */
  private _alUnir = (loteTempIdA: string, loteTempIdB: string): void => {
    const registroId = this._borrador.registroId;
    if (!registroId) return;

    void (async () => {
      try {
        // Las operaciones en vuelo primero, por lo mismo que en `_alDividir`.
        await this._sync?.confirmarAsync();
        const { lotes } = await registroFincaTempGrpcService.unirLotesAsync(
          registroId,
          loteTempIdA,
          loteTempIdB,
        );
        if (this._disposed) return;

        this._lotesDeLaBase(lotes);
      } catch (error) {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("No se pudo unir", parseWizardErrorMessage(error));
      }
    })();
  };

  /** El usuario movió, insertó o quitó un vértice: la operación va al servidor,
   *  que es el que valida y la aplica a TODOS los lotes que comparten ese punto.
   *
   *  Si el servidor rechaza, no tocó nada: se avisa y la pantalla queda como
   *  estaba. Antes la validación corría en el navegador con turf, así que el
   *  servidor recibía la geometría ya cambiada sin forma de verificarla.
   *
   *  El padre y el perímetro se rehacen del lado del servidor, dentro del
   *  mismo rpc (ver `_alDibujar`). */
  private _alEditarVertice = (operacion: OperacionVerticeServidor): void => {
    const registroId = this._borrador.registroId;
    if (!registroId) return;

    void (async () => {
      try {
        // Las operaciones en vuelo primero, por lo mismo que en `_alDividir`.
        await this._sync?.confirmarAsync();
        const { lotes } =
          await registroFincaTempGrpcService.editarVerticeLoteAsync(
            registroId,
            operacion,
          );
        if (this._disposed) return;

        this._lotesDeLaBase(lotes);
      } catch (error) {
        if (this._disposed) return;
        serviceProvider
          .getErrorService()
          .addError("No se pudo editar el lote", parseWizardErrorMessage(error));
      }
    })();
  };

  /** Deja el estado y el mapa con los lotes que devolvió el servidor.
   *
   *  **El mapa repinta sólo la zona que cambió** (2026-10-27, pedido del
   *  usuario): antes repintaba todo lo visible —medido en pruebas, 22 imágenes
   *  por un solo corte—. La zona sale de comparar los lotes de antes con los que
   *  volvieron (`_zonaQueCambio`). **Para revertir:** `refrescarLotes()` sin
   *  argumento acá y en el `LotesTempSync` de `connectBody`. */
  private _lotesDeLaBase(lotes: RegistroFincaBorradorLote[]): void {
    const antes = this._huellasDeLotes();
    this._state.init(lotes.filter((lote) => lote.id).map(aSeed));
    this._sync?.adoptar();
    this._mapRender?.refrescarLotes(
      this._zonaQueCambio(antes, this._huellasDeLotes()),
    );
  }

  /** Lo que el WMS dibuja de cada lote del estado —geometría y nombre—, por su
   *  id del servidor, con su caja. Los lotes sin id todavía no están en la base,
   *  así que el WMS tampoco los dibuja. */
  private _huellasDeLotes(): Map<string, HuellaLote> {
    const huellas = new Map<string, HuellaLote>();
    for (const entry of this._state.getAll()) {
      if (!entry.tempId) continue;
      huellas.set(entry.tempId, {
        firma: `${entry.nombreLote}|${JSON.stringify(entry.polygon.coordinates)}`,
        caja: fx_cajaDePoligono(entry.polygon),
      });
    }
    return huellas;
  }

  /** La caja de lo que cambió entre dos lecturas: los lotes que salieron, los
   *  que entraron y los que cambiaron de geometría o de nombre (de esos, la
   *  caja de antes y la de ahora). `null` —repintar todo, como antes— si no
   *  había nada con qué comparar o si no se encontró ningún cambio: ante la
   *  duda se repinta de más, nunca de menos. */
  private _zonaQueCambio(
    antes: Map<string, HuellaLote>,
    despues: Map<string, HuellaLote>,
  ): [number, number, number, number] | null {
    if (antes.size === 0) return null;
    let zona: [number, number, number, number] | null = null;
    for (const [id, previa] of antes) {
      const actual = despues.get(id);
      if (!actual) zona = fx_unirCajas(zona, previa.caja);
      else if (actual.firma !== previa.firma) {
        zona = fx_unirCajas(fx_unirCajas(zona, previa.caja), actual.caja);
      }
    }
    for (const [id, actual] of despues) {
      if (!antes.has(id)) zona = fx_unirCajas(zona, actual.caja);
    }
    return zona;
  }

  /** Un "Dividir" o "Unir" no se pudo guardar: se avisa y se recargan los
   *  lotes de la base, para no seguir trabajando sobre algo que no existe. */
  private _alFallarGuardado = (error: unknown): void => {
    serviceProvider
      .getErrorService()
      .addError("Error al guardar los lotes", parseWizardErrorMessage(error));
    this._cargarDesdeBase();
  };

  disposeBody(): void {
    this._disposed = true;
    this._mapRender?.dispose();
  }

  getFooterOptions(): RegistroFincaFooterOptions {
    return {
      // En modo edición NO hay "Volver": este paso es el punto de entrada
      // ("Editar finca" abre acá) y los pasos 1–3 no aplican — el nombre, el
      // tipo y el área de una finca ya creada no se tocan desde este flujo.
      // Sin `volver` el modal oculta el botón (ver `RegistroFincaFooterOptions`).
      volver: this._borrador.esEdicion ? null : { onClick: this._onVolverClick },
      submit: { label: "Continuar →", onClick: this._onSubmitClick },
    };
  }

  /** El aside del paso. Se guarda la instancia porque el mapa le habla: al
   *  clickear un lote hay que traer su input de nombre a la vista
   *  (`enfocarInputDe`). */
  getInstructions(): IConnectRender | null {
    this._instructions = new Paso4InstructionsRender(
      this._state,
      (id) => this._mapRender?.setHighlighted(id),
      () => this._mapRender?.setHighlighted(null),
    );
    return this._instructions;
  }

  private _instructions: Paso4InstructionsRender | null = null;

  private _onSubmitClick = (): void => {
    const registroId = this._borrador.registroId;
    if (!registroId) return;
    this._salir(
      "registroFincaSubmit",
      () => registroFincaTempGrpcService.avanzarPasoAsync(registroId, 4),
      () => {
        this._borrador.ultimoPaso = Math.max(this._borrador.ultimoPaso, 4);
        this._onLotesListos(this._borrador);
      },
    );
  };

  /** "Volver" también espera lo pendiente: el paso al que se vuelve lee de
   *  la base, así que tiene que encontrar ahí los cortes hechos acá. */
  private _onVolverClick = (): void => {
    this._salir("registroFincaVolver", () => Promise.resolve(), this._onVolver);
  };

  /** Sale del paso: espera la cola de guardado (resincronizando si algo
   *  falló), corre `confirmar` en el servidor y recién ahí navega. En error
   *  se queda en el paso. */
  private _salir(
    botonId: string,
    confirmar: () => Promise<void>,
    navegar: () => void,
  ): void {
    const sync = this._sync;
    if (this._disposed || this._guardando || !sync) return;

    this._guardando = true;
    const boton = document.getElementById(botonId) as HTMLButtonElement | null;
    if (boton) boton.disabled = true;

    sync
      .confirmarAsync()
      .then(confirmar)
      .then(() => {
        if (this._disposed) return;
        navegar();
      })
      .catch((error) => {
        this._guardando = false;
        if (boton) boton.disabled = false;
        serviceProvider
          .getErrorService()
          .addError("Error al guardar los lotes", parseWizardErrorMessage(error));
      });
  }
}

function aSeed(lote: RegistroFincaBorradorLote): LoteSplitSeed {
  return {
    polygon: lote.areaGeograficaPolygon,
    loteOrigenId: lote.loteOrigenId,
    tempId: lote.id,
    nombreLote: lote.nombreLote,
    asignacion: {
      isAreaProductiva: lote.isAreaProductiva,
      cultivoId: lote.cultivoId,
      coberturaId: lote.coberturaId,
    },
  };
}
